import {
  clampMenuToViewport,
  ContextMenuItem,
  ContextMenuSurface,
  KebabButton,
} from '@src/components/common/contextMenuStyles';
import DeleteIcon from '@src/svgs/DeleteIcon';
import InfoIcon from '@src/svgs/InfoIcon';
import MoreVertIcon from '@src/svgs/MoreVertIcon';
import PencilIcon from '@src/svgs/PencilIcon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useOutsideAlerter } from '../../transcript/useClickOutside';
import FolderIcon from './FolderIcon';

import type { FC, MouseEvent } from 'react';

type Props = {
  onMove: () => void;
  onRename: () => void;
  onDelete: () => void;
  onDetails: () => void;
};

const MENU_WIDTH = 180;
const MENU_HEIGHT = 160;

const StoryRowActionsMenu: FC<Props> = ({
  onMove,
  onRename,
  onDelete,
  onDetails,
}) => {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const portalRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setPos(null), []);
  useOutsideAlerter(portalRef, close);

  useEffect(() => {
    if (!pos) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [pos, close]);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (pos) {
      close();
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setPos(
      clampMenuToViewport(
        rect.right - MENU_WIDTH,
        rect.bottom + 4,
        MENU_WIDTH,
        MENU_HEIGHT,
      ),
    );
  };

  const wrap = (cb: () => void) => (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    close();
    cb();
  };

  return (
    <>
      <KebabButton
        onClick={handleClick}
        data-testid="story-row-actions-trigger"
        aria-label="Story actions"
      >
        <MoreVertIcon />
      </KebabButton>
      {pos &&
        createPortal(
          <ContextMenuSurface
            ref={portalRef}
            style={{ top: pos.y, left: pos.x }}
            data-testid="story-row-actions-menu"
          >
            <ContextMenuItem
              onClick={wrap(onMove)}
              data-testid="story-action-move"
            >
              <FolderIcon /> Move to Folder…
            </ContextMenuItem>
            <ContextMenuItem
              onClick={wrap(onRename)}
              data-testid="story-action-rename"
            >
              <PencilIcon strokeColor="currentColor" /> Rename
            </ContextMenuItem>
            <ContextMenuItem
              onClick={wrap(onDetails)}
              data-testid="story-action-details"
            >
              <InfoIcon /> Details
            </ContextMenuItem>
            <ContextMenuItem
              $danger
              onClick={wrap(onDelete)}
              data-testid="story-action-delete"
            >
              <DeleteIcon /> Delete
            </ContextMenuItem>
          </ContextMenuSurface>,
          document.body,
        )}
    </>
  );
};

export default StoryRowActionsMenu;
